import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import {
  injectLiveChatBootstrap,
  parseLiveChatLicense,
} from "@packages/shared-utils";

import { useAuth } from "@/contexts/AuthContext";
import type { AuthUser } from "@/contexts/AuthContext";
import { isProfessionalInvitePath } from "@/lib/profession-invite-path";

function normalizePathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

/**
 * No LiveChat on sign-in / sign-up / recovery / invitation flows (incl.
 * magic-link verify), nor inside the RFQ wizard.
 *
 * THE WIZARD IS THE ONE NON-AUTH EXCLUSION, added 2026-10-13 at the user's
 * request, and it is here for a different reason than the others. Those hide
 * the widget because the visitor is mid-authentication and a support bubble is
 * noise. `/requests/new` hides it because the wizard's whole job is an intake
 * CONVERSATION — the composer is the primary action on the screen, and a second
 * chat bubble floating over it offers a competing place to type. A visitor who
 * takes the bubble instead describes their trámite to support and never files
 * an RFQ, which is a conversion silently rerouted into an inbox.
 *
 * It comes back everywhere else in the portal. This is a per-path exclusion,
 * not a feature flag: the moment they land on /requests, the bubble returns.
 *
 * THIS REPLACES A NARROWER RULE and keeps its finding. The widget was already
 * hidden here for ANONYMOUS visitors, because it overlapped the sticky
 * "Continuar" CTA on mobile; authenticated users kept it on the grounds that
 * their layout had room. That branch is now unreachable — this check runs
 * first and covers both — so it was deleted rather than left to read as live
 * logic. The mobile overlap it describes is real and is a second reason this
 * exclusion is right, not just a layout quirk of the logged-out shell.
 */
function shouldHideLiveChatOnPath(pathname: string): boolean {
  const p = normalizePathname(pathname);
  // The RFQ wizard — see the docblock. Exact match only: /requests and every
  // other request screen keep the widget.
  if (p === "/requests/new") {
    return true;
  }
  if (p === "/login" || p === "/register" || p === "/verify-email") {
    return true;
  }
  if (
    p === "/auth/forgot-password" ||
    p === "/auth/reset-password" ||
    p === "/auth/verify" ||
    p === "/auth/verify-root-invite" ||
    p === "/auth/sso/callback"
  ) {
    return true;
  }
  if (p === "/accept-invitation" || p.startsWith("/accept-invitation/")) {
    return true;
  }
  // Every profession's acceptance flow, matched by shape rather than by name:
  // the two hardcoded prefixes this replaces would have left a third
  // profession's applicant with the support widget over their signup form.
  if (isProfessionalInvitePath(p)) {
    return true;
  }
  return false;
}

function hideWidgetWhenReady(widget: LiveChatWidgetApi): void {
  const hide = (): void => {
    widget.call("hide");
  };
  hide();
  widget.once("ready", hide);
}

function isPlatformOperator(user: AuthUser): boolean {
  return user.platformRole === "root" || user.platformRole === "root_admin";
}

/**
 * When true: do not load or show the widget (auth/invite flows, unverified email, platform admins).
 */
function shouldHideLiveChat(
  pathname: string,
  user: AuthUser | null,
  isLoading: boolean
): boolean {
  if (shouldHideLiveChatOnPath(pathname)) {
    return true;
  }
  if (isLoading) {
    return false;
  }
  if (user != null && user.emailVerifiedAt == null) {
    return true;
  }
  if (user != null && isPlatformOperator(user)) {
    return true;
  }
  return false;
}

function visitorDisplayName(user: AuthUser): string {
  if (user.displayName?.trim()) {
    return user.displayName.trim();
  }
  const parts = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return parts || user.email;
}

function syncPortalVisitor(
  widget: LiveChatWidgetApi,
  user: AuthUser | null
): void {
  if (user) {
    widget.call("set_customer_name", visitorDisplayName(user));
    widget.call("set_customer_email", user.email);
    widget.call("set_session_variables", {
      gestionalo_app: "portal",
      user_id: user.id,
      tenant_id: user.tenantId ?? "",
      role: user.role,
      is_notary: user.isNotary ? "true" : "false",
      is_accountant: user.isAccountant ? "true" : "false",
      platform_role: user.platformRole ?? "",
    });
    return;
  }
  widget.call("set_customer_name", "");
  widget.call("set_customer_email", "");
  widget.call("set_session_variables", { gestionalo_app: "portal" });
}

export function LiveChatBridge(): JSX.Element | null {
  const { user, isLoading } = useAuth();
  const { pathname } = useLocation();
  const license = parseLiveChatLicense(import.meta.env.VITE_LIVECHAT_LICENSE);

  /**
   * Load tracking.js only when we know the user is allowed to see chat (not invite flows, not root/support, not unverified).
   * Wait for `isLoading` so we do not inject on `/` before session resolves (avoids widget on verify-email redirect).
   */
  useEffect(() => {
    if (license === null) {
      return;
    }
    if (isLoading) {
      return;
    }
    if (shouldHideLiveChat(pathname, user, isLoading)) {
      return;
    }
    injectLiveChatBootstrap(license);
  }, [license, pathname, user, isLoading]);

  useEffect(() => {
    if (license === null) {
      return;
    }
    const widget = window.LiveChatWidget;
    if (widget === undefined) {
      return;
    }
    if (shouldHideLiveChatOnPath(pathname)) {
      widget.call("set_customer_name", "");
      widget.call("set_customer_email", "");
      widget.call("set_session_variables", { gestionalo_app: "portal" });
      return;
    }
    if (isLoading) {
      return;
    }
    if (shouldHideLiveChat(pathname, user, isLoading)) {
      widget.call("set_customer_name", "");
      widget.call("set_customer_email", "");
      widget.call("set_session_variables", { gestionalo_app: "portal" });
      return;
    }
    syncPortalVisitor(widget, user);
  }, [license, pathname, user, isLoading]);

  useEffect(() => {
    if (license === null) {
      return;
    }

    const applyVisibility = (mode: "initial" | "retry"): void => {
      const widget = window.LiveChatWidget;
      if (widget === undefined) {
        return;
      }
      if (shouldHideLiveChatOnPath(pathname)) {
        if (mode === "initial") {
          hideWidgetWhenReady(widget);
        } else {
          widget.call("hide");
        }
        return;
      }
      if (isLoading) {
        return;
      }
      if (shouldHideLiveChat(pathname, user, isLoading)) {
        if (mode === "initial") {
          hideWidgetWhenReady(widget);
        } else {
          widget.call("hide");
        }
        return;
      }
      widget.call("minimize");
    };

    applyVisibility("initial");
    const retryDelaysMs = [350, 1500] as const;
    const timeoutIds = retryDelaysMs.map((ms) =>
      window.setTimeout(() => applyVisibility("retry"), ms)
    );

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
    };
  }, [license, pathname, user, isLoading]);

  if (license === null) {
    return null;
  }

  return (
    <noscript>
      <a href={`https://www.livechat.com/chat-with/${license}/`} rel="nofollow">
        Chat with us
      </a>
      , powered by{" "}
      <a
        href="https://www.livechat.com/?welcome"
        rel="noopener nofollow"
        target="_blank"
      >
        LiveChat
      </a>
    </noscript>
  );
}
